import { BaseElement } from 'base';
import { productVariant } from 'product-variant';
import { formatMoney } from 'format-money';
import { safeUrl } from 'safe-url';

const NO_HOVER = '(hover: none)';
const REDUCE = '(prefers-reduced-motion: reduce)';
const LOADED = 'cevoid-gallery-loaded';
const OPEN = 'data-hotspot-open';
const DISMISSED = 'data-hotspot-dismissed';
const EDGE = 1;
const CARD = ".cevoid-grid > [role='listitem']";
const WRAPPER = '.cevoid-tag-wrapper';
const PORTAL_ID = 'cevoid-modal-portal';
const PRICE_CLAIM = 'data-influencer-price';
const COLOR_CLAIM = 'data-influencer-color';
const PROFILE_CLAIM = 'data-influencer-profile';
const NODE = 'data-influencer-node';
const PORTAL_HOTSPOTS_OFF = 'data-influencer-hotspots-off';
const PRICE = `:is(.cevoid-price, .cevoid-tooltip-price):not([${PRICE_CLAIM}])`;
const PRICE_SOURCE = ':scope > :is(.cevoid-sale-price, .cevoid-original-price)';
const TITLE = '.cevoid-product-title';
const PROFILE = `.cevoid-username:not([${PROFILE_CLAIM}])`;
const PROFILE_NAME = ':scope > .notranslate';
const PROFILE_LINK = ':scope > a[href]';
const SOURCE = "a[href*='variant=']";
const PRODUCT_SOURCE = "a[href*='/products/']";
const SOURCE_SCOPE = '.cevoid-product-link, .cevoid-tag-wrapper';
const ORIGINAL = 'cevoid-original-price';
const PRICE_SLOT = '{{price}}';
const DISCOUNTED = 'cevoid-is-discounted';
const DONE = 'done';
const FALLBACK = 'fallback';
const NONE = 'none';
const PENDING = 'pending';
const PROBING = 'probing';
const FEED_MARGIN = '50% 0px';
const IDLE_TIMEOUT = 1500;
const ACTIVATE_KEYS = ['Enter', ' '];
const STEPS = { prev: -1, next: 1 };

let popupOwner = null;

class InfluencerBlock extends BaseElement {
  #viewport = null;
  #feed = null;
  #top = null;
  #open = null;
  #frame = 0;
  #moneyFormat = '';
  #brand = '';
  #labels = { sale: '', regular: '', from: '' };
  #profile = { name: '', url: null, avatar: null };
  #portal = null;
  #pending = new Map();
  #hotspotsOff = false;

  onConnect() {
    this.#viewport = this.q('[data-viewport]');
    this.#feed = this.q('[data-feed]');
    if (!this.#viewport || !this.#feed) return;

    this.#top = null;
    this.#open = null;
    this.#frame = 0;
    this.#hotspotsOff = this.hasAttribute('data-hotspots-off');

    this.listen(this.#viewport, 'scroll', this.#onScroll, { passive: true });
    this.cleanup(() => {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    });

    const resize = this.observe(
      new ResizeObserver(() => this.#sync()),
      this.#viewport
    );
    resize.observe(this.#feed);

    this.listen(window, LOADED, this.#onLoaded);
    this.listen(this, 'click', this.#onClick, true);
    this.listen(document, 'pointerdown', this.#onDocumentPointerdown);
    this.listen(document, 'keydown', this.#onKeydown);
    this.listen(this, 'focusin', this.#onFocusin);
    this.listen(this, 'focusout', this.#clearDismissed);
    this.listen(this, 'mouseout', this.#clearDismissed);

    const gallery = window.cevoid?.galleries?.[this.#feed.dataset.gallery];
    if (gallery) this.#applyContent(gallery.hasContent);

    if (window.Shopify?.designMode && this.#feed.childElementCount === 0) {
      window.cevoid?.load?.();
    }

    this.#bindTop();

    this.#setupPatching();
  }

  #setupPatching() {
    const { dataset } = this;
    this.#moneyFormat = dataset.moneyFormat ?? '';
    this.#brand = (dataset.cevoidUsername ?? '').trim().toLowerCase();
    this.#labels = {
      sale: dataset.priceSaleLabel ?? '',
      regular: dataset.priceRegularLabel ?? '',
      from: dataset.priceFrom ?? ''
    };
    this.#profile = {
      name: (dataset.profileName ?? '').trim(),
      url: safeUrl(dataset.profileUrl),
      avatar:
        this.q('template[data-profile-avatar]')?.content.firstElementChild ??
        null
    };
    this.#portal = null;

    this.cleanup(() => {
      for (const [node, token] of this.#pending) {
        node.removeAttribute(token.attr);
      }
      this.#pending.clear();
      if (popupOwner === this) {
        popupOwner = null;
        this.#portal?.removeAttribute(PORTAL_HOTSPOTS_OFF);
      }
    });

    this.listen(this, 'click', this.#onFeedInteraction, true);
    this.listen(this, 'keydown', this.#onFeedInteraction, true);

    this.#gatePatching();
    this.#attachPortal();
  }

  #gatePatching() {
    let started = false;
    let idle = 0;
    let timer = 0;
    const cancel = () => {
      if (idle) cancelIdleCallback(idle);
      clearTimeout(timer);
    };
    const start = () => {
      if (started || !this.isConnected) return;
      started = true;
      cancel();
      gate.disconnect();
      this.#watch(this.#feed, () => true);
    };
    const gate = this.observe(
      new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) start();
        },
        { rootMargin: FEED_MARGIN }
      ),
      this.#viewport
    );
    const schedule = () => {
      if (window.requestIdleCallback) {
        idle = requestIdleCallback(start, { timeout: IDLE_TIMEOUT });
      } else {
        timer = setTimeout(start, IDLE_TIMEOUT);
      }
    };

    this.cleanup(cancel);
    if (document.readyState === 'complete') schedule();
    else this.listen(window, 'load', schedule, { once: true });
  }

  #onFeedInteraction(event) {
    if (event.type === 'keydown' && !ACTIVATE_KEYS.includes(event.key)) return;
    if (!this.#feed.contains(event.target)) return;
    popupOwner = this;
    this.#syncPortalHotspots();
    if (this.#portal) this.#patch(this.#portal);
  }

  #syncPortalHotspots() {
    if (popupOwner !== this) return;
    this.#portal?.toggleAttribute(PORTAL_HOTSPOTS_OFF, this.#hotspotsOff);
  }

  #ownsPopup() {
    return popupOwner === this || popupOwner === null;
  }

  #attachPortal() {
    const portal = document.getElementById(PORTAL_ID);
    if (portal) {
      this.#watchPortal(portal);
      return;
    }
    const waiting = this.observe(
      new MutationObserver(() => {
        const found = document.getElementById(PORTAL_ID);
        if (!found) return;
        waiting.disconnect();
        this.#watchPortal(found);
      }),
      document.body,
      { childList: true }
    );
  }

  #watchPortal(portal) {
    this.#portal = portal;
    this.#syncPortalHotspots();
    this.#watch(portal, () => this.#ownsPopup());
  }

  #watch(root, allowed) {
    const run = () => {
      if (allowed()) this.#patch(root);
    };
    this.observe(new MutationObserver(run), root, {
      childList: true,
      subtree: true
    });
    run();
  }

  #patch(root) {
    for (const container of root.querySelectorAll(PRICE)) {
      this.#patchPrice(container);
    }
    for (const title of root.querySelectorAll(TITLE)) {
      const holder = title.parentElement;
      if (holder && !holder.hasAttribute(COLOR_CLAIM)) this.#patchColor(title);
    }
    for (const username of root.querySelectorAll(PROFILE)) {
      this.#patchProfile(username);
    }
  }

  #claim(node, attr, state) {
    const token = { attr };
    node.setAttribute(attr, state);
    this.#pending.set(node, token);
    return token;
  }

  #settle(node, attr, state) {
    node.setAttribute(attr, state);
    this.#pending.delete(node);
  }

  async #resolve(node, attr, empty, eligible) {
    const scope = eligible ? node.closest(SOURCE_SCOPE) : null;
    const exact = scope?.querySelector(SOURCE);
    const href = (exact ?? scope?.querySelector(PRODUCT_SOURCE))?.href;
    if (!href) {
      node.setAttribute(attr, empty);
      return null;
    }
    const token = this.#claim(node, attr, exact ? PENDING : PROBING);
    const data = await productVariant.find(href);
    if (this.#pending.get(node) !== token) return null;
    if (!node.isConnected) {
      this.#pending.delete(node);
      return null;
    }
    if (data) return data;
    this.#settle(node, attr, empty);
    return null;
  }

  async #patchPrice(container) {
    const eligible =
      this.#moneyFormat !== '' &&
      container.querySelector(PRICE_SOURCE) !== null;
    const data = await this.#resolve(
      container,
      PRICE_CLAIM,
      FALLBACK,
      eligible
    );
    if (!data) return;
    const current = this.#priceText(data);
    if (!current) {
      this.#settle(container, PRICE_CLAIM, FALLBACK);
      return;
    }
    const compare =
      data.compareAtPrice === null
        ? ''
        : formatMoney(data.compareAtPrice, this.#moneyFormat);
    container.append(...this.#priceNodes(current, compare, data.from));
    this.#settle(container, PRICE_CLAIM, DONE);
  }

  #priceText(data) {
    const price = formatMoney(data.price, this.#moneyFormat);
    if (!price || !data.from) return price;
    const { from } = this.#labels;
    if (!from.includes(PRICE_SLOT)) return '';
    return from.replace(PRICE_SLOT, () => price);
  }

  #priceNodes(current, compare, range) {
    const { sale, regular } = this.#labels;
    if (!compare) {
      return [
        this.#pricePart(
          ORIGINAL,
          range ? '' : regular,
          'div',
          `notranslate ${ORIGINAL}`,
          current
        )
      ];
    }
    return [
      this.#pricePart(
        'cevoid-sale-price',
        sale,
        'span',
        'notranslate',
        current
      ),
      this.#pricePart(
        `${ORIGINAL} ${DISCOUNTED}`,
        regular,
        'div',
        `notranslate ${ORIGINAL} ${DISCOUNTED}`,
        compare
      )
    ];
  }

  #pricePart(className, label, tag, valueClass, value) {
    const part = this.#node('div', className);
    if (label) part.append(this.#element('span', 'visually-hidden', label));
    part.append(this.#element(tag, valueClass, value));
    return part;
  }

  async #patchColor(title) {
    const holder = title.parentElement;
    const data = await this.#resolve(holder, COLOR_CLAIM, NONE, true);
    if (!data) return;
    if (!data.color || title.parentElement !== holder) {
      this.#settle(holder, COLOR_CLAIM, NONE);
      return;
    }
    title.after(this.#node('div', 'cevoid-product-category', data.color));
    this.#settle(holder, COLOR_CLAIM, DONE);
  }

  #patchProfile(username) {
    const cevoidName = username.querySelector(PROFILE_NAME);
    const original = cevoidName?.textContent ?? '';
    const handle = original.trim().replace(/^@/, '').toLowerCase();
    if (!this.#brand || handle !== this.#brand) return;

    const { name, url, avatar } = this.#profile;
    const parts = [];
    if (name) {
      cevoidName.after(this.#node('span', 'notranslate', name));
      parts.push('name');
    }
    if (avatar) {
      username.prepend(avatar.cloneNode(true));
      parts.push('avatar');
    }
    const link = username.querySelector(PROFILE_LINK);
    if (link && url) {
      link.href = url;
      parts.push('link');
    }
    const label = link?.getAttribute('aria-label') ?? '';
    if (name && original && label.endsWith(original)) {
      link.setAttribute(
        'aria-label',
        `${label.slice(0, -original.length)}${name}`
      );
    }
    if (parts.length > 0) username.setAttribute(PROFILE_CLAIM, parts.join(' '));
  }

  #node(tag, className, text) {
    const node = this.#element(tag, className, text);
    node.setAttribute(NODE, '');
    return node;
  }

  #element(tag, className, text) {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  async #bindTop() {
    const top = this.q('block-top');
    if (!top) return;
    await customElements.whenDefined('block-top');
    if (!this.isConnected) return;
    this.#top = top.run(({ reason }) => {
      if (STEPS[reason]) this.#step(STEPS[reason]);
    });
    this.#sync();
  }

  #onLoaded(event) {
    const { gallery, numberOfPosts } = event.detail ?? {};
    if (gallery !== this.#feed.dataset.gallery) return;
    this.#applyContent(numberOfPosts > 0);
  }

  #applyContent(hasContent) {
    this.hidden = this.hasAttribute('data-hide-empty') && !hasContent;
    this.#sync();
  }

  #onScroll() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#sync();
    });
  }

  #step(direction) {
    const cards = this.qa(CARD);
    const step =
      cards.length > 1
        ? Math.abs(cards[1].offsetLeft - cards[0].offsetLeft)
        : this.#viewport.clientWidth;
    const sign = getComputedStyle(this.#viewport).direction === 'rtl' ? -1 : 1;
    const behavior = window.matchMedia(REDUCE).matches ? 'auto' : 'smooth';
    this.#viewport.scrollBy({ left: step * direction * sign, behavior });
  }

  #sync() {
    const viewport = this.#viewport;
    const max = viewport.scrollWidth - viewport.clientWidth;
    const position = Math.abs(viewport.scrollLeft);
    const scrollable = max > EDGE;
    this.#top?.setArrowState({
      prevDisabled: !scrollable || position <= EDGE,
      nextDisabled: !scrollable || position >= max - EDGE,
      hidden: !scrollable
    });
  }

  #onClick(event) {
    if (this.#hotspotsOff) return;
    if (this.#open && !this.#open.isConnected) this.#open = null;

    const tag = event.target.closest('.cevoid-tag');
    if (!tag) {
      if (!event.target.closest('.cevoid-tag-inner')) this.#close();
      return;
    }

    const wrapper = tag.closest(WRAPPER);
    if (!wrapper || wrapper === this.#open) return;
    if (!window.matchMedia(NO_HOVER).matches) return;

    event.preventDefault();
    event.stopPropagation();
    this.#close();
    wrapper.setAttribute(OPEN, '');
    this.#open = wrapper;
  }

  #close() {
    this.#open?.removeAttribute(OPEN);
    this.#open = null;
  }

  #onDocumentPointerdown(event) {
    if (!this.#open || this.#open.contains(event.target)) return;
    this.#close();
  }

  #onKeydown(event) {
    if (event.key !== 'Escape' || this.#hotspotsOff) return;

    const focused = this.contains(event.target)
      ? event.target.closest(WRAPPER)
      : null;
    const wrapper = focused ?? this.#open ?? this.q(`${WRAPPER}:hover`);
    if (!wrapper) return;

    if (focused && event.target.closest('.cevoid-tag-inner')) {
      wrapper.querySelector('.cevoid-tag > a')?.focus({ preventScroll: true });
    }
    wrapper.setAttribute(DISMISSED, '');
    if (wrapper === this.#open) this.#close();
  }

  #onFocusin(event) {
    event.target.closest?.(WRAPPER)?.removeAttribute(DISMISSED);
  }

  #clearDismissed(event) {
    const wrapper = event.target.closest?.(`[${DISMISSED}]`);
    if (!wrapper || wrapper.contains(event.relatedTarget)) return;
    wrapper.removeAttribute(DISMISSED);
  }
}

if (!customElements.get('influencer-block')) {
  customElements.define('influencer-block', InfluencerBlock);
}
